import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useUpdateAppTag, AppTag } from "@/hooks/useAppTags";

interface EditTagModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tag: AppTag | null;
}

export function EditTagModal({ open, onOpenChange, tag }: EditTagModalProps) {
  const [tagName, setTagName] = useState("");
  const [tagDescription, setTagDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [createdBy, setCreatedBy] = useState("");
  const [notes, setNotes] = useState("");

  const updateMutation = useUpdateAppTag();

  // Populate form when tag changes
  useEffect(() => {
    if (tag) {
      setTagName(tag.tag_name);
      setTagDescription(tag.tag_description);
      setIsActive(tag.is_active);
      setCreatedBy(tag.created_by || "");
      setNotes(tag.notes || "");
    }
  }, [tag]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tag) return;

    // Validation
    if (!tagName.trim()) {
      return;
    }
    if (!tagDescription.trim()) {
      return;
    }
    if (tagName.trim().length > 255) {
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: tag.id,
        formData: {
          tag_name: tagName,
          tag_description: tagDescription,
          is_active: isActive,
          created_by: createdBy || undefined,
          notes: notes || undefined,
        },
        currentVersion: tag.version_number,
      });

      onOpenChange(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleCancel = () => {
    if (tag) {
      setTagName(tag.tag_name);
      setTagDescription(tag.tag_description);
      setIsActive(tag.is_active);
      setCreatedBy(tag.created_by || "");
      setNotes(tag.notes || "");
    }
    onOpenChange(false);
  };

  if (!tag) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
            <DialogDescription>
              Update the tag details. Changes will increment the version number.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Current Version</Label>
                <div className="mt-1">
                  <Badge variant="secondary">v{tag.version_number}</Badge>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="is-active">Active</Label>
                <Switch
                  id="is-active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-tag-name">
                Tag Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-tag-name"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                placeholder="Enter tag name"
                maxLength={255}
                required
              />
              {tagName.trim().length === 0 && tagName.length > 0 && (
                <p className="text-sm text-red-500">Tag name is required</p>
              )}
              {tagName.trim().length > 255 && (
                <p className="text-sm text-red-500">Tag name must be 255 characters or less</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-tag-description">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="edit-tag-description"
                value={tagDescription}
                onChange={(e) => setTagDescription(e.target.value)}
                placeholder="Enter tag description"
                rows={3}
                required
              />
              {tagDescription.trim().length === 0 && tagDescription.length > 0 && (
                <p className="text-sm text-red-500">Tag description is required</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-created-by">Created By</Label>
              <Input
                id="edit-created-by"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                placeholder="Enter your name (optional)"
                maxLength={255}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes (optional)"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                updateMutation.isPending ||
                !tagName.trim() ||
                !tagDescription.trim() ||
                tagName.trim().length > 255
              }
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
